import React from 'react'
import { Invoice } from '../../../../back-end/common/invoice'
import { AppContext } from '../../App'
import { PageStatus } from '../../constants/pageStatus'

import * as Request from '../../utilities/request'
import { Client } from '../../../../back-end/utilities/apiDefinitions'
import { Col, Container, FormLabel, Row } from 'react-bootstrap'

import CloseIcon from '../../images/icons/close_panel.svg'
import { FormSelect, SelectOption } from '../ui/Form/Select'
import { ArrowLeftCircle, ChevronDown, ChevronRight, Minus, Search } from 'react-feather'
import { DateTime } from 'luxon'

import './lineItemHistoryPanel.css'
import { Loading } from '../ui/Loading/Loading'

interface LineItemHistoryPanelProps {
	isOpen: boolean
	onClose: () => void
	currentInvoiceID: string
	currentInvoiceClientID: string
	insertLineItem: (desc: string) => void
}

const LineItemHistoryPanel = (props: LineItemHistoryPanelProps) => {
	const context = React.useContext(AppContext)
	const [clientFetchStatus, setClientFetchStatus] = React.useState<PageStatus>('Loading')
	const [invoiceFetchStatus, setInvoiceFetchStatus] = React.useState<PageStatus>('Loading')

	const [clients, setClients] = React.useState<Client[]>([])
	const [selectedClientID, setSelectedClientID] = React.useState(props.currentInvoiceClientID)
	const [recentInvoices, setRecentInvoices] = React.useState<Invoice[]>([])
	const [expandedInvoices, setExpandedInvoices] = React.useState<Record<string, boolean>>({})

	React.useEffect(() => {
		const getData = async () => {
			Request.getPagedData(`client`, 4000, (data) => {
				setClients((clients) => [...(clients || []), ...(data as { clients: Client[] }).clients])
				setClientFetchStatus('Ready')
			})
		}

		setClientFetchStatus('Loading')
		if (context.appState.authState.isLoggedIn && props.currentInvoiceClientID) {
			getData()
		}
	}, [context.appState.authState, props.currentInvoiceID, props.currentInvoiceClientID])

	React.useEffect(() => {
		const getData = async () => {
			const invoiceRes = await Request.get(`invoice?where=ClientID==${selectedClientID}`)
			if (invoiceRes.data.success && invoiceRes.data.invoices.length > 0) {
				setRecentInvoices(invoiceRes.data.invoices.filter((invoice: Invoice) => invoice.invoiceID !== props.currentInvoiceID))
				setInvoiceFetchStatus('Ready')
			} else {
				setInvoiceFetchStatus('Error')
			}
		}

		setInvoiceFetchStatus('Loading')
		if (context.appState.authState.isLoggedIn && selectedClientID) {
			getData()
		}
	}, [context.appState.authState, selectedClientID])

	const toggleExpanded = (id: string) => {
		setExpandedInvoices({ ...expandedInvoices, [id]: !expandedInvoices[id] })
	}

	const containerClassName = `mr-3 invoice-history-panel ${!props.isOpen ? 'panel-closed' : ''}`

	if (clientFetchStatus === 'Loading') {
		return (
			<div className={containerClassName}>
				<Loading show />
			</div>
		)
	}

	const InvoiceRows = () => {
		if (invoiceFetchStatus === 'Loading') {
			return (
				<div className="center-flex p-5">
					<Loading show />
				</div>
			)
		}

		if (recentInvoices.length === 0) {
			return (
				<div className="center-flex p-5">
					<span className="span-black span-body-2">No recent invoices</span>
				</div>
			)
		}

		return (
			<>
				{recentInvoices.map((invoice) => (
					<React.Fragment key={invoice.invoiceID}>
						<Row className="py-2 align-items-center">
							<Col sm={1} className="center-flex">
								{invoice.lineItems && invoice.lineItems?.length > 0 ? (
									expandedInvoices[invoice.invoiceID] ? (
										<ChevronDown size={20} onClick={() => toggleExpanded(invoice.invoiceID)} />
									) : (
										<ChevronRight size={20} onClick={() => toggleExpanded(invoice.invoiceID)} />
									)
								) : (
									<Minus size={24} color={'#fff'} />
								)}
							</Col>
							<Col sm={5}>
								<span className="span-black span-body-2">{DateTime.fromISO(invoice.date).toFormat('dd/MM/yyyy') || ''}</span>
							</Col>
							<Col sm={6}>
								<span className="span-bold span-black span-body-2">{invoice.number}</span>
							</Col>
						</Row>
						{expandedInvoices[invoice.invoiceID] &&
							invoice.lineItems?.map((lineItem) => (
								<Row key={`${invoice.invoiceID}_${lineItem.invoiceLineID}`} className="py-2 align-items-center">
									<Col sm={1} className="center-flex">
										<ArrowLeftCircle
											size={20}
											className="insert-btn"
											onClick={() => {
												props.insertLineItem(lineItem.description)
												props.onClose()
											}}
										/>
									</Col>
									<Col sm={11}>
										<span className="span-black span-body-2">{lineItem.description}</span>
									</Col>
								</Row>
							))}
					</React.Fragment>
				))}
			</>
		)
	}

	return (
		<Container className={containerClassName}>
			<Row className="pb-3">
				<Col onClick={() => props.onClose()} className="panel-close-btn" sm="auto">
					<img src={CloseIcon} alt="Close" className="pr-2" />
					<span className="span-bold span-navy span-body-2">Close</span>
				</Col>
			</Row>
			<Row className="pb-3">
				<Col>
					<FormSelect
						name="client"
						label="Client"
						placeholder={
							<div className="d-flex align-items-center">
								<Search size={14} />
								<span className="pl-2">Search or select a client</span>
							</div>
						}
						onChange={(e) => setSelectedClientID(e ? (e as SelectOption<string>).value : '')}
						options={clients.map((c) => ({ value: c.clientID, label: c.name })).sort((a, b) => (a.label < b.label ? -1 : 1))}
						value={selectedClientID}
					/>
				</Col>
			</Row>
			<Row>
				<Col>
					<FormLabel>Recent Invoices</FormLabel>
				</Col>
			</Row>
			<Row className="px-3">
				<Col>
					<Row className="invoice-header py-2">
						<Col sm={1}></Col>
						<Col sm={5}>
							<span className="span-bold span-navy span-body-2">Date</span>
						</Col>
						<Col sm={6}>
							<span className="span-bold span-navy span-body-2">Invoice No.</span>
						</Col>
					</Row>
					<InvoiceRows />
				</Col>
			</Row>
		</Container>
	)
}

export default LineItemHistoryPanel
